import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { endPoints } from "../../Config";
import { handleGetRequest } from "../../service/GetTemplate";
import "./JetinnoDashboard.scss";

const stateOptions = [
  { label: "Todos", value: "" },
  { label: "En línea", value: "true" },
  { label: "Fuera de línea", value: "false" },
];
const warningOptions = [
  { label: "Todas", value: "" },
  { label: "Con alertas", value: "true" },
  { label: "Sin alertas", value: "false" },
];

const formatDate = (value) => {
  if (!value) return "No disponible";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "No disponible" : date.toLocaleString();
};

const warningSeverity = (severity) =>
  severity === "critical" ? "danger" : "warning";
const supplySeverity = (status) => {
  if (status === "empty") return "danger";
  if (status === "unavailable") return "warning";
  return "info";
};
const supplyStatus = {
  empty: "Agotado",
  unavailable: "No disponible",
  reported: "Reportado",
};

const MetricCard = ({ title, value, icon, tone = "primary" }) => (
  <Card className={`jetinno-metric jetinno-metric--${tone}`}>
    <div className="jetinno-metric__icon">
      <i className={icon} />
    </div>
    <div>
      <span>{title}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  </Card>
);

const JetinnoDashboard = () => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
  });
  const [online, setOnline] = useState("");
  const [hasWarnings, setHasWarnings] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const loadSummary = useCallback(async () => {
    const response = await handleGetRequest(
      endPoints.jetinnoSummary,
      false,
      false,
      dispatch
    );
    setSummary(response?.data || null);
  }, [dispatch]);

  const loadDevices = useCallback(
    async (page = 1) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      if (online) params.set("online", online);
      if (hasWarnings) params.set("hasWarnings", hasWarnings);
      if (search.trim()) params.set("search", search.trim());
      const response = await handleGetRequest(
        endPoints.jetinnoDevices(params.toString()),
        false,
        false,
        dispatch
      );
      const data = response?.data;
      setDevices(Array.isArray(data?.items) ? data.items : []);
      setPagination(
        data?.pagination || { page, limit: pagination.limit, total: 0 }
      );
    },
    [dispatch, hasWarnings, online, pagination.limit, search]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSummary(), loadDevices(1)]);
    setLoading(false);
  }, [loadDevices, loadSummary]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDetail = async (machineNo) => {
    const response = await handleGetRequest(
      endPoints.jetinnoDevice(machineNo),
      false,
      false,
      dispatch
    );
    if (response?.data) setDetail(response.data);
  };

  const stale =
    summary?.freshness &&
    (summary.freshness.devicesStale ||
      summary.freshness.warningsStale ||
      summary.freshness.suppliesStale);

  return (
    <div className="jetinno-dashboard">
      <div className="jetinno-header">
        <div>
          <span className="jetinno-eyebrow">Telemetría de máquinas</span>
          <h1>Jetinno</h1>
          <p>
            Estado operativo, alertas y suministros. Vista exclusivamente de
            consulta.
          </p>
        </div>
        <Button
          label="Actualizar"
          icon="pi pi-refresh"
          onClick={refresh}
          loading={loading}
        />
      </div>

      <div
        className={`jetinno-freshness ${
          stale ? "jetinno-freshness--warning" : "jetinno-freshness--ok"
        }`}
      >
        <i
          className={`pi ${
            stale ? "pi-exclamation-triangle" : "pi-check-circle"
          }`}
        />
        <div>
          <strong>
            {stale
              ? "Algunos datos están desactualizados"
              : "Datos actualizados"}
          </strong>
          <span>
            Máquinas: {formatDate(summary?.freshness?.devicesObservedAt)} ·
            Alertas: {formatDate(summary?.freshness?.warningsObservedAt)} ·
            Suministros: {formatDate(summary?.freshness?.suppliesObservedAt)}
          </span>
        </div>
      </div>

      <div className="jetinno-metrics">
        <MetricCard
          title="Máquinas"
          value={summary?.devices?.total}
          icon="pi pi-desktop"
        />
        <MetricCard
          title="En línea"
          value={summary?.devices?.online}
          icon="pi pi-wifi"
          tone="success"
        />
        <MetricCard
          title="Fuera de línea"
          value={summary?.devices?.offline}
          icon="pi pi-ban"
          tone="neutral"
        />
        <MetricCard
          title="Alertas activas"
          value={summary?.activeWarnings?.total}
          icon="pi pi-exclamation-triangle"
          tone="danger"
        />
        <MetricCard
          title="Máquinas afectadas"
          value={summary?.activeWarnings?.affectedDevices}
          icon="pi pi-bell"
          tone="warning"
        />
        <MetricCard
          title="Con suministros"
          value={summary?.supplies?.coveredDevices}
          icon="pi pi-box"
          tone="info"
        />
      </div>

      <Card className="jetinno-table-card">
        <div className="jetinno-toolbar">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Máquina o ubicación"
            />
          </span>
          <Dropdown
            value={online}
            options={stateOptions}
            onChange={(event) => setOnline(event.value)}
          />
          <Dropdown
            value={hasWarnings}
            options={warningOptions}
            onChange={(event) => setHasWarnings(event.value)}
          />
          <Button
            label="Aplicar"
            icon="pi pi-filter"
            className="p-button-outlined"
            onClick={() => loadDevices(1)}
          />
        </div>
        <DataTable
          value={devices}
          loading={loading}
          lazy
          paginator
          first={(pagination.page - 1) * pagination.limit}
          rows={pagination.limit}
          totalRecords={pagination.total}
          onPage={(event) => loadDevices(event.page + 1)}
          emptyMessage="No se encontraron máquinas"
          responsiveLayout="scroll"
        >
          <Column field="machineNo" header="Máquina" />
          <Column
            field="displayLocation"
            header="Ubicación"
            body={(row) => (
              <div>
                <span>{row.displayLocation || "—"}</span>
                {row.syncroMachine ? (
                  <small className="jetinno-syncro-match">
                    Vinculada con SYNCRO
                  </small>
                ) : null}
              </div>
            )}
          />
          <Column
            header="Conectividad"
            body={(row) => (
              <Tag
                severity={row.online ? "success" : "danger"}
                value={row.online ? "En línea" : "Fuera de línea"}
              />
            )}
          />
          <Column field="activeWarningCount" header="Alertas" />
          <Column
            field="observedAt"
            header="Observado"
            body={(row) => formatDate(row.observedAt)}
          />
          <Column
            body={(row) => (
              <Button
                icon="pi pi-eye"
                className="p-button-text"
                aria-label={`Ver ${row.machineNo}`}
                onClick={() => openDetail(row.machineNo)}
              />
            )}
          />
        </DataTable>
      </Card>

      <Dialog
        header={`Máquina ${detail?.machineNo || ""}`}
        visible={Boolean(detail)}
        onHide={() => setDetail(null)}
        style={{ width: "min(760px, 95vw)" }}
      >
        {detail && (
          <div className="jetinno-detail">
            <div>
              <span>Ubicación</span>
              <strong>{detail.displayLocation || "—"}</strong>
            </div>
            <div>
              <span>Estado</span>
              <Tag
                severity={detail.online ? "success" : "danger"}
                value={detail.online ? "En línea" : "Fuera de línea"}
              />
            </div>
            <div>
              <span>Registro SYNCRO</span>
              <strong>
                {detail.syncroMachine
                  ? `Vinculada · ${detail.syncroMachine.serialNumber}`
                  : "Sin coincidencia exacta"}
              </strong>
            </div>
            <h3>Alertas activas</h3>
            {detail.warnings?.length ? (
              detail.warnings.map((warning, index) => (
                <div
                  className="jetinno-warning"
                  key={`${warning.code}-${index}`}
                >
                  <div>
                    <Tag
                      severity={warningSeverity(warning.severity)}
                      value={
                        warning.severity === "critical"
                          ? "Crítica"
                          : "Advertencia"
                      }
                    />
                    <strong>{warning.title}</strong>
                    <small>{warning.code}</small>
                  </div>
                  <span>{warning.description}</span>
                </div>
              ))
            ) : (
              <p>No hay alertas activas.</p>
            )}
            <h3>Suministros</h3>
            {detail.supplies?.materials?.length ? (
              <div className="jetinno-balances">
                {detail.supplies.materials.map((material) => (
                  <div key={material.code}>
                    <span>{material.label}</span>
                    <strong>
                      {material.status === "unavailable"
                        ? "—"
                        : material.rawBalance}
                    </strong>
                    <Tag
                      severity={supplySeverity(material.status)}
                      value={supplyStatus[material.status]}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p>No hay información de suministros.</p>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default JetinnoDashboard;
